interface SelectOption<T extends string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string> {
  /** Current value */
  value: T;
  /** Available options */
  options: SelectOption<T>[];
  /** Callback when value changes */
  onChange: (value: T) => void;
  /** Label for the select */
  label: string;
  /** Description text */
  description?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Optional className */
  className?: string;
}

/**
 * Select dropdown component
 */
export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  description,
  disabled = false,
  className = ''
}: SelectProps<T>) {
  return (
    <div className={`select-container ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {description && (
        <p className="text-xs text-gray-500 mb-2">{description}</p>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        className={`
          block w-full px-3 py-2 text-sm
          bg-white border border-gray-300 rounded-lg
          shadow-sm
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:bg-gray-100 disabled:cursor-not-allowed
          ${disabled ? 'text-gray-400' : 'text-gray-700'}
        `}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Inline select for compact layouts
 */
export function SelectInline<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled = false,
  className = ''
}: Omit<SelectProps<T>, 'description'>) {
  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <label className="text-sm text-gray-600 whitespace-nowrap">
        {label}:
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
        className={`
          px-2 py-1 text-sm
          bg-white border border-gray-300 rounded
          focus:outline-none focus:ring-1 focus:ring-blue-500
          disabled:bg-gray-100 disabled:cursor-not-allowed
        `}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
